import { useState, useEffect } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import LabelCardForm from '@RegistriesForms/LabelCardForm';
import DeleteLabelCardForm from '@RegistriesForms/DeleteLabelCardForm';
import CreateLeadsForm from '@FormForms/CreateLeadsForm';
import {
    Trash2,
    Copy,
    Eye,
    Tag,
    Sparkles,
    Pencil,
    ChevronRight
} from 'lucide-react';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import './OptionsMenu.scss';

export default function OptionsMenu(props) {

    const [showDeleteForm, setShowDeleteForm] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showUpdateForm, setShowUpdateForm] = useState(false);
    const [showDuplicateForm, setShowDuplicateForm] = useState(false);
    const [currentUserProjectPermissions, setCurrentUserProjectPermissions] = useState({});

    const {
        getUserPermissionByProjectId
    } = useReduxProject();

    const className = 'optionsMenu';

    useEffect(() => {
        if (props.item?.projectId) {
            setCurrentUserProjectPermissions(getUserPermissionByProjectId(props.item.projectId));
        }
    }, [props.item?.projectId]);

    const closeMenu = () => {
        props.closePopup?.();
    };

    const handleView = () => {
        props?.handleOpenView?.(props?.item);
        closeMenu();
    };

    const handleDelete = () => {
        setShowDeleteForm(true);
        closeMenu();
    };

    const handleCloseDelete = () => {
        setShowDeleteForm(false);
    };

    const handleCreate = (e) => {
        e?.preventDefault();
        setShowCreateForm(true);
        closeMenu();
    };

    const handleCloseCreate = () => {
        setShowCreateForm(false);
    };

    const handleUpdate = (e) => {
        e?.preventDefault();
        setShowUpdateForm(true);
        closeMenu();
    };

    const handleCloseUpdate = () => {
        setShowUpdateForm(false);
    }

    const handleDuplicate = (e) => {
        e?.preventDefault();
        setShowDuplicateForm(true);
        closeMenu();
    };

    const handleCloseDuplicate = () => {
        setShowDuplicateForm(false);
    }

    const canCreate = Boolean(currentUserProjectPermissions?.allowCreate);

    const canUpdate = Boolean(currentUserProjectPermissions?.allowEdit);

    const canDelete = Boolean(currentUserProjectPermissions?.allowAdmin);

    return (
        <div className={className}>

            {/* Context Menu */}
            <Menu
                open={Boolean(props.open)}
                onClose={closeMenu}
                anchorEl={props.anchorEl}
                anchorReference="anchorPosition"
                anchorPosition={props.anchorPosition}
                className={`${className}__menu`}
                MenuListProps={{
                    disablePadding: true,
                    className: `${className}__menuList`
                }}
                PaperProps={{
                    className: `${className}__paper`,
                    elevation: 0
                }}
                slotProps={{
                    paper: {
                        sx: {
                            overflow: 'visible'
                        }
                    }
                }}
            >

                {/* Header */}
                {props.type === 'label' && (
                    <div className={`${className}__header`}>

                        <div className={`${className}__header__icon`}>
                            <Tag size={16} />
                        </div>

                        <div className={`${className}__header__content`}>
                            <span className={`${className}__header__eyebrow`}>
                                Filter
                            </span>

                            <span className={`${className}__header__name`}>
                                {props?.item?.name || 'Untitled'}
                            </span>
                        </div>

                    </div>
                )}

                {/* Actions */}
                <div className={`${className}__actions`}>

                    {/* View */}
                    {props?.showView && (
                        <MenuItem
                            className={`${className}__item`}
                            onClick={handleView}
                        >
                            <div className={`${className}__item__icon ${className}__item__icon--blue`}>
                                <Eye size={17} />
                            </div>

                            <div className={`${className}__item__content`}>
                                <span className={`${className}__item__title`}>
                                    View
                                </span>
                                <span className={`${className}__item__description`}>
                                    Open and view details
                                </span>
                            </div>

                            <ChevronRight
                                size={15}
                                className={`${className}__item__arrow`}
                            />
                        </MenuItem>
                    )}

                    {/* Create */}
                    {props?.showCreate && canCreate && (
                        <MenuItem
                            className={`${className}__item`}
                            onClick={handleCreate}
                        >
                            <div className={`${className}__item__icon ${className}__item__icon--purple`}>
                                <Sparkles size={17} />
                            </div>

                            <div className={`${className}__item__content`}>
                                <span className={`${className}__item__title`}>
                                    {props.type === 'label'
                                        ? 'Create Filter'
                                        : 'Create'}
                                </span>

                                <span className={`${className}__item__description`}>
                                    Create a new item
                                </span>
                            </div>

                            <ChevronRight
                                size={15}
                                className={`${className}__item__arrow`}
                            />
                        </MenuItem>
                    )}

                    {props?.showUpdate && canUpdate && (
                        <MenuItem
                            className={`${className}__item`}
                            onClick={handleUpdate}
                        >
                            <div className={`${className}__item__icon ${className}__item__icon--gray`}>
                                <Pencil size={17} />
                            </div>

                            <div className={`${className}__item__content`}>
                                <span className={`${className}__item__title`}>
                                    Edit
                                </span>

                                <span className={`${className}__item__description`}>
                                    Make changes to this item
                                </span>
                            </div>

                            <ChevronRight
                                size={15}
                                className={`${className}__item__arrow`}
                            />
                        </MenuItem>
                    )}

                    {/* Duplicate */}
                    {props?.showDuplicate && canCreate && (
                        <MenuItem
                            className={`${className}__item`}
                            onClick={handleDuplicate}
                        >
                            <div className={`${className}__item__icon ${className}__item__icon--gray`}>
                                <Copy size={17} />
                            </div>

                            <div className={`${className}__item__content`}>
                                <span className={`${className}__item__title`}>
                                    Duplicate
                                </span>

                                <span className={`${className}__item__description`}>
                                    Create a copy of this item
                                </span>
                            </div>

                            <ChevronRight
                                size={15}
                                className={`${className}__item__arrow`}
                            />
                        </MenuItem>
                    )}

                </div>

                {/* Delete */}
                {props?.item?.active && canDelete && (
                    <div className={`${className}__dangerSection`}>

                        <div className={`${className}__separator`} />

                        <MenuItem
                            className={`${className}__item ${className}__item--danger`}
                            onClick={handleDelete}
                        >
                            <div className={`${className}__item__icon ${className}__item__icon--red`}>
                                <Trash2 size={17} />
                            </div>

                            <div className={`${className}__item__content`}>
                                <span className={`${className}__item__title`}>
                                    Delete
                                </span>

                                <span className={`${className}__item__description`}>
                                    Permanently remove this item
                                </span>
                            </div>

                            <ChevronRight
                                size={15}
                                className={`${className}__item__arrow`}
                            />
                        </MenuItem>

                    </div>
                )}

            </Menu>

            {/* Create Form */}
            {showCreateForm && (
                <>
                    {props.type === 'label' && (
                        <LabelCardForm
                            open={showCreateForm}
                            closePopup={handleCloseCreate}
                            reloadData={props?.renderData}
                        />
                    )}

                    {props.type === 'leads' && (
                        <CreateLeadsForm
                            open={showCreateForm}
                            onClose={handleCloseCreate}
                            reloadData={props?.reloadData}
                        />
                    )}
                </>
            )}

            {showUpdateForm && (
                <>
                    {props.type === 'label' && (
                        <LabelCardForm
                            open={showUpdateForm}
                            closePopup={handleCloseUpdate}
                            item={props?.item}
                            type='update'
                            reloadData={props?.renderData}
                        />
                    )}

                    {props.type === 'leads' && (
                        <CreateLeadsForm
                            open={showUpdateForm}
                            onClose={handleCloseUpdate}
                            item={props?.item}
                            type='update'
                            reloadData={props?.reloadData}
                        />
                    )}
                </>
            )}

            {showDuplicateForm && (
                <>
                    {props.type === 'label' && (
                        <LabelCardForm
                            open={showDuplicateForm}
                            closePopup={handleCloseDuplicate}
                            item={props?.item}
                            type='duplicate'
                            reloadData={props?.renderData}
                        />
                    )}

                    {props.type === 'leads' && (
                        <CreateLeadsForm
                            open={showDuplicateForm}
                            onClose={handleCloseDuplicate}
                            item={props?.item}
                            type='duplicate'
                            reloadData={props?.reloadData}
                        />
                    )}
                </>
            )}

            {/* Delete Form */}
            {showDeleteForm && props.type === 'label' && (
                <DeleteLabelCardForm
                    open={showDeleteForm}
                    closePopup={handleCloseDelete}
                    item={props?.item}
                    labelCardList={props?.labelCardList}
                    reloadData={props?.reloadData}
                />
            )}

        </div>
    );
}
